import { Injectable, computed, inject, signal } from '@angular/core';
import { VideosUploadService, VideoMetadata, VideoUploadProgress } from 'src/app/features/video/controller/video-upload.service';

export interface PendingVideoItem {
    id: string; // Unique ID for list management
    file: File;
    recordPdf: File | null;
    title: string;
    description: string;
    category: string;
    competition: string;
    gender: string;
    seasonId: number | null;
    matchDate: string | null;
    uploadProgress: number;
    uploadStatus: 'pending' | 'uploading' | 'processing' | 'complete' | 'error';
    errorMessage?: string;
    videoId?: string; // Stored if successful
    resolutionWidth?: number;
    resolutionHeight?: number;
    minResolutionOk?: boolean;
}

export interface UploadState {
    videos: PendingVideoItem[];
    isUploadingAll: boolean;
    uploadFinished: boolean;
    globalStatusText: string;
    editingVideoId: string | null;
}

const initialState: UploadState = {
    videos: [],
    isUploadingAll: false,
    uploadFinished: false,
    globalStatusText: '',
    editingVideoId: null
};

@Injectable({ providedIn: 'root' })
export class VideoUploadStore {

    private videosUploadService = inject(VideosUploadService);

    private _state = signal<UploadState>(initialState);

    // Selectors
    readonly videos = computed(() => this._state().videos);
    readonly isUploadingAll = computed(() => this._state().isUploadingAll);
    readonly uploadFinished = computed(() => this._state().uploadFinished);
    readonly globalStatusText = computed(() => this._state().globalStatusText);
    readonly editingVideoId = computed(() => this._state().editingVideoId);
    readonly editingVideo = computed(() => this.videos().find(v => v.id === this.editingVideoId()) || null);

    // Computed metrics
    readonly totalVideos = computed(() => this.videos().length);
    readonly completedVideos = computed(() => this.videos().filter(v => v.uploadStatus === 'complete').length);
    readonly pendingVideosCount = computed(() => this.videos().filter(v => v.uploadStatus === 'pending').length);
    readonly hasPendingVideos = computed(() => this.videos().some(v => v.uploadStatus === 'pending'));
    readonly globalProgress = computed(() => {
        const list = this.videos();
        if (list.length === 0) return 0;
        const totalProgress = list.reduce((acc, curr) => acc + curr.uploadProgress, 0);
        return Math.round(totalProgress / list.length);
    });

    // Actions
    clear() {
        this._state.set(initialState);
    }
    addVideo(
        item: Omit<PendingVideoItem, 'id' | 'uploadProgress' | 'uploadStatus'>,
        options?: { initialStatus?: PendingVideoItem['uploadStatus']; errorMessage?: string }
    ) {
        const initialStatus = options?.initialStatus ?? 'pending';
        const newItem: PendingVideoItem = {
            ...item,
            id: crypto.randomUUID(), // Simple UUID
            uploadProgress: 0,
            uploadStatus: initialStatus,
            errorMessage: options?.errorMessage
        };

        this._state.update(state => ({
            ...state,
            videos: [...state.videos, newItem]
        }));
    }

    removeVideo(id: string) {
        this._state.update(state => ({
            ...state,
            videos: state.videos.filter(v => v.id !== id)
        }));
    }

    updateVideo(id: string, updates: Partial<PendingVideoItem>) {
        this._state.update(state => ({
            ...state,
            videos: state.videos.map(v => v.id === id ? { ...v, ...updates } : v)
        }));
    }

    setEditingVideo(id: string | null) {
        this._state.update(state => ({ ...state, editingVideoId: id }));
    }

    // Effect action to upload all pending videos
    async uploadAll() {
        if (this._state().isUploadingAll || this.videos().length === 0 || !this.hasPendingVideos()) return;

        this._state.update(state => ({
            ...state,
            isUploadingAll: true,
            globalStatusText: 'Iniciando subida de videos...'
        }));

        const currentVideos = this.videos();

        // We upload sequentially, as backend processes in threads anyway
        for (let i = 0; i < currentVideos.length; i++) {
            const video = currentVideos[i];

            // Skip videos that should not be uploaded in this batch
            if (video.uploadStatus !== 'pending') continue;

            const resolutionValidation = await this.ensureResolutionValid(video.id);
            if (!resolutionValidation.valid) {
                this.updateVideo(video.id, {
                    uploadStatus: 'error',
                    uploadProgress: 0,
                    errorMessage: resolutionValidation.message,
                    resolutionWidth: resolutionValidation.width,
                    resolutionHeight: resolutionValidation.height,
                    minResolutionOk: false
                });
                continue;
            }

            this._state.update(state => ({
                ...state,
                globalStatusText: `Subiendo video ${i + 1} de ${currentVideos.length}: ${video.title}`
            }));

            const metadata: VideoMetadata = {
                title: video.title,
                description: video.description,
                category: video.category,
                competition: video.competition,
                gender: video.gender,
                duration_in_seconds: 0, // Will be set or read later if needed
                season_id: video.seasonId ?? 1,
                match_date: video.matchDate || undefined
            };

            try {
                await this.videosUploadService.uploadVideo(
                    video.file,
                    metadata,
                    video.recordPdf,
                    (progress: VideoUploadProgress) => {
                        this.updateVideo(video.id, {
                            uploadProgress: progress.progress,
                            uploadStatus: progress.status === 'processing' ? 'processing' : progress.status as any,
                            errorMessage: progress.status === 'error' ? progress.message : undefined
                        });
                    }
                );

                // Mark as complete in our store representation
                this.updateVideo(video.id, {
                    uploadStatus: 'complete',
                    uploadProgress: 100
                });

            } catch (error: any) {
                console.error(`Error uploading video ${video.title}:`, error);
                this.updateVideo(video.id, {
                    uploadStatus: 'error',
                    errorMessage: error.message || 'Error desconocido'
                });
                // Stop uploading the rest if there's a critical error? 
                // Depending on requirements, we can continue or break.
                // For now, let's keep going with the next one.
            }
        }

        this._state.update(state => ({
            ...state,
            isUploadingAll: false,
            uploadFinished: true,
            globalStatusText: 'Proceso de subida finalizado'
        }));
    }

    private async ensureResolutionValid(videoId: string): Promise<{
        valid: boolean;
        width?: number;
        height?: number;
        message?: string;
    }> {
        const video = this.videos().find(v => v.id === videoId);
        if (!video) {
            return { valid: false, message: 'No se encontró el vídeo en la cola.' };
        }

        let width = video.resolutionWidth;
        let height = video.resolutionHeight;

        if (!width || !height) {
            const metadata = await this.readVideoResolution(video.file);
            width = metadata.width;
            height = metadata.height;
            this.updateVideo(videoId, {
                resolutionWidth: width,
                resolutionHeight: height
            });
        }

        const minDimension = Math.min(width, height);
        if (minDimension < 720) {
            return {
                valid: false,
                width,
                height,
                message: `Calidad insuficiente (${width}x${height}). Solo se permiten vídeos de 720p o superior.`
            };
        }

        return { valid: true, width, height };
    }

    private readVideoResolution(file: File): Promise<{ width: number; height: number }> {
        return new Promise((resolve, reject) => {
            const videoElement = document.createElement('video');
            videoElement.preload = 'metadata';
            const objectUrl = URL.createObjectURL(file);

            const cleanup = () => {
                URL.revokeObjectURL(objectUrl);
                videoElement.removeAttribute('src');
                videoElement.load();
            };

            videoElement.onloadedmetadata = () => {
                const width = videoElement.videoWidth || 0;
                const height = videoElement.videoHeight || 0;
                cleanup();
                resolve({ width, height });
            };

            videoElement.onerror = () => {
                cleanup();
                reject(new Error('No se pudo leer la resolución del vídeo.'));
            };

            videoElement.src = objectUrl;
        });
    }
}
